import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Building2, ScrollText, LogOut, Plus, FlaskConical, GraduationCap, Apple, KeyRound, Bell, CheckCheck, Smartphone, X, ShieldCheck, Zap, BookOpen } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import HistoryNavButtons from "../../components/HistoryNavButtons";
import { Button, SoundToggle } from "../../components/ui-kit";
import api from "../../lib/api";
import { formatItalianDateTime } from "../../lib/dateUtils";

// Notifiche per il consulente: modulo ricette/documenti inviato dal cliente,
// non conformità aperta, primo accesso di un utente cliente. Stessa API e
// stesso modello (app_notifications) già usati per la campanella cliente,
// qui senza scadenze lotti/adempimenti che non riguardano il consulente.
function AdminNotifications() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const fetchNotifications = () => api.get("/app/notifications").then(({ data }) => setItems(data.items || [])).catch(() => {});
  useEffect(() => {
    fetchNotifications();
    const t = setInterval(fetchNotifications, 60000);
    const onVisibility = () => { if (!document.hidden) fetchNotifications(); };
    window.addEventListener("focus", fetchNotifications);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", fetchNotifications);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const unread = items.filter((item) => !item.read).length;
  const displayCount = unread > 99 ? "99+" : unread;

  const markRead = async (item) => {
    if (!item.read) {
      await api.patch(`/app/notifications/${item.id}/read`);
      setItems((current) => current.map((entry) => entry.id === item.id ? { ...entry, read: true } : entry));
    }
    if (item.link) { setOpen(false); nav(item.link); }
  };
  const markAll = async () => {
    await api.post("/app/notifications/read-all");
    setItems((current) => current.map((item) => ({ ...item, read: true })));
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`relative grid h-11 w-11 place-items-center rounded-lg text-white/80 hover:bg-white/10 ${unread > 0 ? "text-white" : ""}`}
        aria-label={`Notifiche: ${unread}`}
        aria-expanded={open}
        data-testid="admin-notif-bell"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && <span
          className="absolute right-0.5 top-0.5 grid h-[18px] min-w-[18px] place-items-center rounded-full border-2 border-navy bg-red-600 px-1 font-body text-[9px] font-bold leading-none text-white"
          aria-hidden="true"
          data-testid="admin-notif-badge"
        >{displayCount}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed inset-x-3 top-[calc(4.5rem+env(safe-area-inset-top))] z-50 max-h-[calc(100dvh-6rem-env(safe-area-inset-bottom))] overflow-hidden rounded-xl bg-white text-navy shadow-card ring-1 ring-hairline sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[380px] sm:max-w-[92vw]" data-testid="admin-notif-dropdown">
            <div className="flex items-center justify-between gap-3 border-b border-hairline px-4 py-3">
              <div>
                <div className="font-heading text-sm font-semibold text-navy">Attività clienti</div>
                <div className="font-body text-xs text-slate-400">{unread ? `${unread} da leggere` : "Nessuna novità"}</div>
              </div>
              {unread > 0 && <button type="button" onClick={markAll} className="inline-flex min-h-[36px] items-center gap-1 rounded-lg px-2 text-xs font-semibold text-blue-700 hover:bg-blue-50" data-testid="admin-notif-read-all"><CheckCheck className="h-4 w-4" /> Spunta tutte</button>}
            </div>
            <div className="max-h-96 divide-y divide-hairline overflow-y-auto">
              {items.map((item) => (
                <button key={item.id} type="button" onClick={() => markRead(item)} className={`block w-full px-4 py-3 text-left ${item.read ? "opacity-60" : "bg-blue-50/40"}`} data-testid={`admin-notif-item-${item.id}`}>
                  <div className="text-sm font-semibold text-navy">{item.title}</div>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{item.message}</p>
                  {item.created_at && <div className="mt-1 text-[10px] text-slate-400">{formatItalianDateTime(item.created_at)}</div>}
                </button>
              ))}
              {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">Nessuna notifica presente.</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function pushKeyToBytes(value) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const binary = window.atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

// Attivazione notifiche push sul cellulare del consulente: stesso
// meccanismo (Web Push/VAPID) già collaudato per i titolari clienti, qui
// riservato al super_admin. Minimizzabile, si ricorda la scelta sul dispositivo.
function AdminPushSetup() {
  const [state, setState] = useState("checking");
  const [testing, setTesting] = useState(false);
  const [reportTesting, setReportTesting] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("df_admin_push_setup_collapsed") === "1"; } catch { return false; }
  });
  useEffect(() => {
    const check = async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) { setState("unsupported"); return; }
      const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
      const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
      if (isIos && !standalone) { setState("install-ios"); return; }
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setState(subscription ? "enabled" : Notification.permission === "denied" ? "denied" : "available");
    };
    check().catch(() => setState("unsupported"));
  }, []);
  if (state === "checking" || state === "unsupported") return null;
  const enable = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { setState("denied"); return; }
      const [{ data }, registration] = await Promise.all([api.get("/app/push/public-key"), navigator.serviceWorker.ready]);
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: pushKeyToBytes(data.public_key) });
      await api.post("/app/push/subscribe", subscription.toJSON());
      setState("enabled");
      toast.success("Notifiche push attivate su questo dispositivo");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Non è stato possibile attivare le notifiche push");
    }
  };
  const disable = async () => {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await api.post("/app/push/unsubscribe", { endpoint: subscription.endpoint });
      await subscription.unsubscribe();
    }
    setState("available");
    toast.success("Notifiche push disattivate su questo dispositivo");
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/app/push/test");
      toast.success("Notifica di prova inviata al tuo cellulare");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invio della notifica di prova non riuscito");
    } finally { setTesting(false); }
  };
  const sendTestReport = async () => {
    setReportTesting(true);
    try {
      const { data } = await api.post("/admin/daily-report/run-now");
      toast.success(`Report inviato via email: ${data.active} clienti su ${data.tenants} con attività oggi`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invio del report non riuscito");
    } finally { setReportTesting(false); }
  };
  const collapse = () => { setCollapsed(true); try { localStorage.setItem("df_admin_push_setup_collapsed", "1"); } catch { /* ignore */ } };
  const expand = () => { setCollapsed(false); try { localStorage.removeItem("df_admin_push_setup_collapsed"); } catch { /* ignore */ } };
  if (collapsed) {
    return <div className={`border-b px-4 py-1.5 sm:px-6 ${state === "enabled" ? "border-emerald-200 bg-emerald-50" : "border-blue-200 bg-blue-50"}`} data-testid="admin-push-setup-collapsed">
      <div className="mx-auto flex max-w-[1500px] items-center">
        <button type="button" onClick={expand} aria-label="Mostra lo stato delle notifiche sul cellulare" title="Notifiche sul cellulare"
          className={`flex h-8 w-8 items-center justify-center rounded-full ${state === "enabled" ? "text-emerald-700 hover:bg-emerald-100" : "text-blue-700 hover:bg-blue-100"}`}>
          <Smartphone className="h-4 w-4" />
        </button>
      </div>
    </div>;
  }
  return <div className={`border-b px-4 py-3 sm:px-6 ${state === "enabled" ? "border-emerald-200 bg-emerald-50" : "border-blue-200 bg-blue-50"}`} data-testid="admin-push-setup">
    <div className="mx-auto flex max-w-[1500px] flex-col gap-3 text-sm sm:flex-row sm:items-center">
      <Bell className={`h-5 w-5 shrink-0 ${state === "enabled" ? "text-emerald-600" : "text-blue-700"}`} />
      <div className="flex-1 text-navy">{state === "enabled" ? <><strong>Notifiche sul cellulare attive.</strong> Ricevi un avviso quando un cliente invia ricette/documenti, apre una non conformità o accede per la prima volta.</> : state === "install-ios" ? <><strong>Attiva le notifiche su iPhone.</strong> Da Safari premi Condividi, "Aggiungi alla schermata Home", poi apri il pannello admin dalla nuova icona.</> : state === "denied" ? <><strong>Notifiche bloccate.</strong> Riattivale dalle impostazioni del browser o del telefono.</> : <><strong>Ricevi le notifiche sul cellulare.</strong> Sapraí subito quando un cliente compila ricette/documenti o apre una non conformità.</>}</div>
      {state === "available" && <button type="button" onClick={enable} className="min-h-[42px] rounded-lg bg-navy px-4 font-semibold text-white hover:bg-navy/90">Attiva notifiche</button>}
      {state === "enabled" && <div className="flex flex-col gap-2 sm:flex-row"><button type="button" disabled={testing} onClick={sendTest} className="min-h-[40px] rounded-lg bg-emerald-700 px-3 font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">{testing ? "Invio…" : "Invia notifica di prova"}</button><button type="button" disabled={testing} onClick={disable} className="min-h-[40px] rounded-lg border border-emerald-400 bg-white px-3 font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60">Disattiva</button></div>}
      {/* Il report giornaliero via email non dipende dalle notifiche push: visibile in ogni stato del banner */}
      <button type="button" disabled={reportTesting} onClick={sendTestReport} data-testid="admin-report-run-now" className="min-h-[40px] shrink-0 rounded-lg border border-navy/20 bg-white px-3 font-semibold text-navy hover:bg-slate-50 disabled:opacity-60">{reportTesting ? "Invio…" : "Invia report di prova ora"}</button>
      <button type="button" onClick={collapse} aria-label="Minimizza questo avviso" title="Minimizza" className="shrink-0 self-start rounded-full p-1.5 text-navy/50 hover:bg-black/5 hover:text-navy sm:self-center">
        <X className="h-4 w-4" />
      </button>
    </div>
  </div>;
}

export default function AdminLayout() {
  const { logout } = useAuth();
  const nav = useNavigate();
  // L'icona "Aggiungi alla schermata Home" di iOS viene letta da questo tag
  // nel momento in cui il consulente la aggiunge: qui la sostituiamo con la
  // variante oro, così l'icona dell'area admin si distingue a colpo d'occhio
  // da quella dell'area cliente (navy) sul telefono. ClientLayout la
  // ripristina alla versione cliente quando si torna in quell'area.
  useEffect(() => {
    const iconLink = document.querySelector('link[rel="apple-touch-icon"]');
    if (iconLink) iconLink.href = "/apple-touch-icon-admin.png";
  }, []);
  const link = ({ isActive }) =>
    `inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body min-h-[44px] transition-colors ${
      isActive ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`;
  return (
    <div className="app-shell min-h-screen min-h-[100dvh] w-full max-w-full overflow-x-clip bg-haccp-bg">
      <header className="sticky top-0 z-30 bg-navy pt-[env(safe-area-inset-top)] text-white">
        <div className="mx-auto flex h-16 max-w-[1500px] min-w-0 items-center gap-2 px-3 sm:gap-4 sm:px-6">
          <HistoryNavButtons variant="dark" />
          <div className="md:hidden"><Logo variant="dark" size="sm" showProduct={false} /></div>
          <div className="hidden md:block"><Logo variant="dark" size="sm" /></div>
          <nav className="hidden md:flex items-center gap-1 ml-4">
            <NavLink to="/admin" end className={link} data-testid="admin-nav-dashboard"><LayoutDashboard className="w-4 h-4" /> Dashboard</NavLink>
            <NavLink to="/admin/report-serale" className={link} data-testid="admin-nav-report-serale"><Zap className="w-4 h-4" /> Report serale</NavLink>
            <NavLink to="/admin/safeguard" className={link} data-testid="admin-nav-safeguard"><ShieldCheck className="w-4 h-4" /> SafeGuard</NavLink>
            <NavLink to="/admin/audit" className={link} data-testid="admin-nav-audit"><ScrollText className="w-4 h-4" /> Audit log</NavLink>
            <NavLink to="/admin/shelf-life-rules" className={link} data-testid="admin-nav-shelf-life"><FlaskConical className="w-4 h-4" /> Shelf Life Expert</NavLink>
            <NavLink to="/admin/academy" className={link} data-testid="admin-nav-academy"><GraduationCap className="w-4 h-4" /> DF Academy</NavLink>
            <NavLink to="/admin/nutrition" className={link} data-testid="admin-nav-nutrition"><Apple className="w-4 h-4" /> Nutrition</NavLink>
            <NavLink to="/admin/archivio-ricette" className={link} data-testid="admin-nav-recipe-library"><BookOpen className="w-4 h-4" /> Ricette</NavLink>
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="success" className="px-3 sm:px-5" onClick={() => nav("/admin/onboarding")} aria-label="Nuova azienda" data-testid="admin-new-company"><Plus className="w-4 h-4" /><span className="hidden min-[390px]:inline">Nuova azienda</span></Button>
            <AdminNotifications />
            <SoundToggle testid="admin-sound-toggle" className="grid h-11 w-11 place-items-center rounded-lg text-white/80 hover:bg-white/10" />
            <button onClick={() => nav("/admin/cambia-password")} className="grid h-11 w-11 place-items-center rounded-lg text-white/80 hover:bg-white/10" aria-label="Cambia password" data-testid="admin-change-password"><KeyRound className="w-5 h-5" /></button>
            <button onClick={() => { logout(); nav("/login"); }} className="grid h-11 w-11 place-items-center rounded-lg text-white/80 hover:bg-white/10" aria-label="Esci" data-testid="admin-logout"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
        <nav className="md:hidden flex items-center gap-1 px-3 pb-2 border-t border-white/10 overflow-x-auto" aria-label="Navigazione amministrazione">
          <NavLink to="/admin" end className={({ isActive }) => `${link({ isActive })} shrink-0`} data-testid="admin-mobile-nav-dashboard">
            <LayoutDashboard className="w-4 h-4" /> Dashboard
          </NavLink>
          <NavLink to="/admin/report-serale" className={({ isActive }) => `${link({ isActive })} shrink-0`} data-testid="admin-mobile-nav-report-serale">
            <Zap className="w-4 h-4" /> Report serale
          </NavLink>
          <NavLink to="/admin/safeguard" className={({ isActive }) => `${link({ isActive })} shrink-0`} data-testid="admin-mobile-nav-safeguard">
            <ShieldCheck className="w-4 h-4" /> SafeGuard
          </NavLink>
          <NavLink to="/admin/audit" className={({ isActive }) => `${link({ isActive })} shrink-0`} data-testid="admin-mobile-nav-audit">
            <ScrollText className="w-4 h-4" /> Audit log
          </NavLink>
          <NavLink to="/admin/shelf-life-rules" className={({ isActive }) => `${link({ isActive })} shrink-0`} data-testid="admin-mobile-nav-shelf-life">
            <FlaskConical className="w-4 h-4" /> Shelf Life
          </NavLink>
          <NavLink to="/admin/academy" className={({ isActive }) => `${link({ isActive })} shrink-0`} data-testid="admin-mobile-nav-academy">
            <GraduationCap className="w-4 h-4" /> Academy
          </NavLink>
          <NavLink to="/admin/nutrition" className={({ isActive }) => `${link({ isActive })} shrink-0`} data-testid="admin-mobile-nav-nutrition">
            <Apple className="w-4 h-4" /> Nutrition
          </NavLink>
          <NavLink to="/admin/archivio-ricette" className={({ isActive }) => `${link({ isActive })} shrink-0`} data-testid="admin-mobile-nav-recipe-library">
            <BookOpen className="w-4 h-4" /> Ricette
          </NavLink>
        </nav>
      </header>
      <AdminPushSetup />
      <main className="app-content mx-auto w-full max-w-[1500px] min-w-0 overflow-x-clip p-3 sm:p-6 lg:p-8"><Outlet /></main>
    </div>
  );
}
